<template>
  <v-layout class="grey lighten-4" column>
    <div class="noto bold pa-3 d-md-flex align-center" width="100%">
      <div class="title primary--text">{{ section }}</div>
      <v-spacer></v-spacer>
      <v-dialog @keydown.esc="createDialog=false" v-model="createDialog" persistent max-width="400px">
        <template v-slot:activator="{ on }">
          <v-btn
            v-if="taxesPermissions['create-tax']"
            v-on="on"
            fab
            x-small
            depressed
            color="primary"
          >
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </template>
        <v-card>
          <v-card-title>
            <span class="headline">Crear impuesto</span>
          </v-card-title>
          <v-card-text>
            <v-form ref="form" v-model="form.valid">
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    v-model="form.data.name"
                    label="Alias"
                    :rules="form.rules.name"
                    rounded
                    outlined
                    dense
                    hide-details
                    required>
                  </v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-select
                    label="Impuesto"
                    :rules="form.rules.satCode"
                    v-model="form.data.satCode"
                    @change="selectTax"
                    return-object
                    :items="taxes"
                    item-text="text"
                    item-value="value"
                    rounded
                    outlined
                    dense
                    hide-details
                    required>
                  </v-select>
                </v-col>
                <v-col cols="12">
                  <v-select
                    label="Tipo de impuesto"
                    :rules="form.rules.isRetention"
                    v-model="form.data.isRetention"
                    :items="taxKinds"
                    item-text="text"
                    item-value="value"
                    rounded
                    outlined
                    dense
                    hide-details
                    required>
                  </v-select>
                </v-col>
                <v-col cols="12">
                  <v-select
                    label="¿Es tasa o cuota?"
                    :rules="form.rules.isQuota"
                    v-model="form.data.isQuota"
                    :items="taxTypes"
                    item-text="text"
                    item-value="value"
                    rounded
                    outlined
                    dense
                    hide-details
                    required>
                  </v-select>
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model.number="form.data.base"
                    :rules="form.rules.base"
                    label="¿De cuánto es la tasa o la cuota?"
                    rounded
                    outlined
                    dense
                    hide-details
                    required>
                  </v-text-field>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="black darken-1" text rounded @click="onCloseCreateDialog">Cerrar</v-btn>
            <v-btn color="primary" rounded @click="submit">Guardar</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>

    <v-flex xs12>
      <v-data-table
        :loading-text="table.title"
        :server-items-length="table.total"
        :loading="table.loading"
        :headers="table.headers"
        :items="table.data"
        :options="table.options"
        @update:options="updateTable"
        class="elevation-1 fixed-header">
        <template v-slot:item.satCode="{ item }">
          <v-chip :color="getColor(item.satCode)" dark small>
            {{ item.kind }}
          </v-chip>
        </template>
        <template v-slot:item.isRetention="{ item }">
          {{ item.isRetention ?  'Retenido' : 'Traslado' }}
        </template>
        <template v-slot:item.isQuota="{ item }">
          {{ item.isQuota ? 'Cuota' : 'Tasa' }}
        </template>
        <template v-slot:item.action="{ item }">
          <div class="d-flex justify-end align-center">
            <v-tooltip left>
              <template
                v-slot:activator="{ on }">
                <v-btn
                  v-if="taxesPermissions['edit-tax']"
                  fab
                  x-small
                  depressed
                  color="primary"
                  class="mr-2"
                  v-on="on"
                  @click="onOpenUpdateTaxDialog(item)">
                  <v-icon>mdi-pencil</v-icon>
                </v-btn>
              </template>
              <span>Editar impuesto</span>
            </v-tooltip>
            <v-tooltip left>
              <template
                v-slot:activator="{ on }">
                <v-btn
                  v-if="taxesPermissions['delete-tax']"
                  fab
                  x-small
                  depressed
                  color="error"
                  v-on="on"
                  @click="onOpenRemoveTaxDialog(item)">
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </template>
              <span>Eliminar impuesto</span>
            </v-tooltip>
          </div>
        </template>
      </v-data-table>
    </v-flex>

    <v-dialog @keydown.esc="removeDialog=false" v-model="removeDialog" persistent max-width="400px">
      <v-card>
        <v-card-title>
          <span>¿Deseas eliminar <b>{{ this.form.data.name }}</b>?</span>
        </v-card-title>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="black darken-1" text rounded @click="onCloseRemoveDialog">Cancelar</v-btn>
          <v-btn  color="primary" rounded @click="remove">Aceptar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-overlay :value="overlay" z-index="999999999">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </v-layout>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep'

export default {
  name: 'Taxes',
  data () {
    return {
      createDialog: false,
      removeDialog: false,
      overlay: false,
      section: this.$i18n.t('taxes.section'),
      table: {
        title: this.$i18n.t('taxes.loading'),
        loading: true,
        headers: [
          { text: this.$i18n.t('taxes.headers.alias'), value: 'name' },
          { text: this.$i18n.t('taxes.headers.kind'), value: 'satCode' },
          { text: this.$i18n.t('taxes.headers.isRetention'), value: 'isRetention', sortable: false },
          { text: this.$i18n.t('taxes.headers.isQuota'), value: 'isQuota', sortable: false },
          { text: this.$i18n.t('taxes.headers.base'), value: 'base', sortable: false },
          { text: this.$i18n.t('taxes.headers.action'), value: 'action', sortable: false }
        ],
        data: [],
        total: 0,
        options: {
          itemsPerPage: 15,
          page: 1
        }
      },
      form: {
        valid: false,
        data: {
          name: null,
          kind: null,
          satCode: null,
          isRetention: null,
          isQuota: null,
          base: 0
        },
        rules: {
          name: [v => v !== null || 'Alias is required'],
          satCode: [v => v !== null || 'Tax is required'],
          isRetention: [v => v !== null || 'TaxKind is required'],
          isQuota: [v => v !== null || 'Quota is required'],
          base: [v => v >= 0 || 'Base is required']
        }
      },
      taxes: [
        // { text: 'ISR', value: '001' },
        { text: 'IVA', value: '002' },
        { text: 'IEPS', value: '003' }
      ],
      taxKinds: [
        { text: 'Trasladado', value: false },
        { text: 'Retenido', value: true }
      ],
      taxTypes: [
        { text: 'Cuota', value: true },
        { text: 'Tasa', value: false }
      ],
      search: ''
    }
  },
  computed: {
    provider () {
      return this.$store.state.currentProvider.data._id
    },
    taxesPermissions () {
      return this.$store.state.login.permissions['my-taxes'].components['taxes-list'].actions
    }
  },
  methods: {
    getColor (tax) {
      return {
        '001': 'red',
        '002': 'secondary',
        '003': 'green'
      }[tax]
    },
    selectTax (tax) {
      this.form.data.kind = tax.text
      this.form.data.satCode = tax.value
    },
    updateTable: async function (data) {
      const { sortBy, sortDesc } = data
      await this.load({
        limit: data.itemsPerPage,
        skip: (data.itemsPerPage * (data.page - 1)),
        sort: `${sortDesc[0] ? '-' : ''}${sortBy[0]}`
      })
    },
    async load (params = null) {
      params = params || {
        limit: this.table.options.itemsPerPage,
        skip: (this.table.options.itemsPerPage * (this.table.page - 1))
      }
      try {
        const response = await this.$api.get(`/v1/providers/${this.provider}/taxes`, params)
        this.table.data = response.data
        this.table.total = response.total
        this.table.loading = false
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
    },
    onCloseCreateDialog: function () {
      if (this.$refs && this.$refs.form) this.$refs.form.reset()
      this.form.data = {}
      this.createDialog = false
    },
    onOpenUpdateTaxDialog: function (data) {
      const form = cloneDeep(data)
      this.form.data = form
      this.createDialog = true
    },
    onOpenRemoveTaxDialog: function (data) {
      this.form.data = cloneDeep(data)
      this.removeDialog = true
    },
    onCloseRemoveDialog: function () {
      this.onCloseCreateDialog()
      this.removeDialog = false
    },
    submit: async function () {
      if (this.$refs.form.validate()) {
        this.overlay = true
        const request = this.form.data._id == null
          ? this.create()
          : this.update()
        try {
          await request
          await this.load()
          this.onCloseCreateDialog()
        } catch (error) {
          this.$toasted.show(error.message, { duration: 3000 })
        }
        this.overlay = false
      }
    },
    create: async function () {
      const payload = this.form.data
      await this.$api.post(`/v1/providers/${this.provider}/taxes`, payload)
      this.$toasted.show('Se ha creado el impuesto correctamente', { duration: 3000 })
    },
    update: async function () {
      const payload = this.form.data
      await this.$api.put(`/v1/providers/${this.provider}/taxes/${this.form.data._id}`, payload)
      this.$toasted.show('Se ha actualizado el impuesto correctamente', { duration: 3000 })
    },
    remove: async function () {
      this.overlay = true
      try {
        await this.$api.del(`/v1/providers/${this.provider}/taxes/${this.form.data._id}`)
        await this.load()
        this.onCloseRemoveDialog()
        this.$toasted.show('Se ha eliminado el impuesto correctamente', { duration: 3000 })
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.overlay = false
    }
  }
}
</script>

<style lang="css" scoped>
</style>
